import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  getTrialBalance,
} from "../../services/accountingService"



const formatMoney = (value, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))


const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("fr-FR").format(date)
}


const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (detail && typeof detail === "object") {
    return detail.message || detail.detail || fallback
  }

  return error?.message || fallback
}



export default function TrialBalancePage() {
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({
    total_debit: 0,
    total_credit: 0,
    difference: 0,
    balanced: true,
  })
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const result = await getTrialBalance({
        date_from: dateFrom,
        date_to: dateTo,
      })

      setRows(result?.items || [])
      setSummary(
        result?.summary || {
          total_debit: 0,
          total_credit: 0,
          difference: 0,
          balanced: true,
        }
      )
    } catch (err) {
      setError(extractErrorMessage(err, "Impossible de charger la balance."))
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo])

  useEffect(() => {
    loadData()
  }, [loadData])

  const columns = useMemo(
    () => [
      { field: "account_code", headerName: "Code", minWidth: 110 },
      { field: "account_name", headerName: "Compte", minWidth: 240, flex: 1 },
      { field: "account_type", headerName: "Type", minWidth: 150 },
      {
        field: "total_debit",
        headerName: "Total débit",
        minWidth: 160,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "total_credit",
        headerName: "Total crédit",
        minWidth: 160,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "balance",
        headerName: "Solde",
        minWidth: 160,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
    ],
    []
  )

  return (
    <MainLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Balance comptable
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Débits, crédits et soldes pour chaque compte.
            </Typography>
          </Box>

          <Chip
            label={summary.balanced ? "Balance équilibrée" : "Écart détecté"}
            color={summary.balanced ? "success" : "error"}
          />
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              label="Du"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />

            <TextField
              size="small"
              label="Au"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />

            <Button variant="outlined" onClick={loadData} disabled={loading}>
              Actualiser
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography color="text.secondary">Total débit</Typography>
            <Typography variant="h5" fontWeight={900}>
              {formatMoney(summary.total_debit)}
            </Typography>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography color="text.secondary">Total crédit</Typography>
            <Typography variant="h5" fontWeight={900}>
              {formatMoney(summary.total_credit)}
            </Typography>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography color="text.secondary">Différence</Typography>
            <Typography variant="h5" fontWeight={900}>
              {formatMoney(summary.difference)}
            </Typography>
          </Paper>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: 650 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.account_id}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              sx={{ border: 0 }}
            />
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  )
}
